// Чистые вспомогательные функции модуля «Видеопоток». Без побочных эффектов и без React.

// ISO (YYYY-MM-DD) → ДД.ММ.ГГГГ; null/пусто → null (в UI отрисуется как «—»).
export function fmtDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

// Значение локализованного поля {ru, kk} для текущего языка.
export function pick(obj, lang) {
  if (obj == null) return "";
  return obj[lang] ?? obj.ru ?? "";
}

// Отображаемое имя файла в текущей выборке.
export function fileName(item) {
  return item?.video?.available ? item.video.file_name : null;
}

// Поиск по сводной таблице (5.11) — по шести отображаемым полям, без учёта регистра.
// Даты сопоставляются в виде ДД.ММ.ГГГГ (ввод «03.2025» находит весь март 2025).
// Служебные поля (id, mime_type, duration_sec, url) в поиске не участвуют.
export function matchesSearch(item, query, lang) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const parts = [
    pick(item.source_name, lang),
    pick(item.address, lang),
    pick(item.location, lang),
    fileName(item) || "", // при available=false файл в поиске не участвует
    fmtDate(item.plan_date) || "",
    fmtDate(item.fact_date) || "",
  ];
  return parts.join(" ").toLowerCase().includes(q);
}

// Сортировочный ключ по колонке. null → трактуется как «пусто» (уходит в конец).
function sortKey(item, col, lang) {
  switch (col) {
    case "source_name":
      return pick(item.source_name, lang);
    case "address":
      return pick(item.address, lang);
    case "location":
      return pick(item.location, lang);
    case "video":
      return fileName(item); // null, если файла нет
    case "plan_date":
      return item.plan_date; // ISO хорошо сортируется лексикографически
    case "fact_date":
      return item.fact_date;
    default:
      return "";
  }
}

// Сортировка сводной таблицы (RQ.22.07). Работает на КОПИИ массива — исходный порядок
// items[] от API не меняется (от него зависят список выбора и перечень видеопотоков).
// Пустые значения («—») всегда в конце, независимо от направления сортировки.
export function sortItems(items, col, dir, lang) {
  return items.slice().sort((a, b) => {
    const x = sortKey(a, col, lang);
    const y = sortKey(b, col, lang);
    const ex = x == null || x === "";
    const ey = y == null || y === "";
    if (ex && ey) return a.id - b.id;
    if (ex) return 1;
    if (ey) return -1;
    if (x < y) return -1 * dir;
    if (x > y) return 1 * dir;
    return a.id - b.id;
  });
}

// Подпись выбранного видеопотока в списке выбора (5.4):
// «{Источник} — {Местоположение} ({Адрес})» — одного источника недостаточно.
export function streamLabel(item, lang) {
  return `${pick(item.source_name, lang)} — ${pick(
    item.location,
    lang
  )} (${pick(item.address, lang)})`;
}

// Доля в процентах: один знак после запятой, десятичный разделитель — запятая.
export function fmtShare(pct) {
  return Number(pct).toFixed(1).replace(".", ",");
}

// Нейтральный постер области воспроизведения (5.5): SVG-заставка с источником и местом.
// В реализации допускается первый кадр записи — тогда poster можно не задавать.
export function posterDataUrl(item, lang) {
  const esc = (s) =>
    String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  const src = esc(pick(item.source_name, lang));
  const sub = esc(`${pick(item.location, lang)} · ${pick(item.address, lang)}`);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="960" height="540">` +
    `<defs><pattern id="g" width="60" height="60" patternUnits="userSpaceOnUse">` +
    `<path d="M60 0H0V60" fill="none" stroke="#1c2331" stroke-width="1"/></pattern></defs>` +
    `<rect width="960" height="540" fill="#0f131b"/><rect width="960" height="540" fill="url(#g)"/>` +
    `<circle cx="480" cy="250" r="120" fill="none" stroke="#26314a" stroke-width="1.5"/>` +
    `<circle cx="480" cy="250" r="60" fill="none" stroke="#26314a" stroke-width="1.5"/>` +
    `<path d="M480 100V400M330 250H630" stroke="#26314a" stroke-width="1.5"/>` +
    `<text x="480" y="466" fill="#7d8ba4" font-family="Segoe UI,sans-serif" font-size="21" text-anchor="middle">${src}</text>` +
    `<text x="480" y="498" fill="#4e5c76" font-family="Segoe UI,sans-serif" font-size="17" text-anchor="middle">${sub}</text></svg>`;
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
}

// Защита от инверсии диапазона дат (5.3): при From > To подтягиваем вторую границу.
export function clampRange(from, to, changed) {
  if (from && to && from > to) {
    return changed === "from" ? { from, to: from } : { from: to, to };
  }
  return { from, to };
}
